import type { SettingsApi } from "@exagens/behavioral-insight-console-client/dist/apis/SettingsApi";
import type { SparxEditorApi } from "@exagens/behavioral-insight-console-client/dist/apis/SparxEditorApi";
import type { SparxsStatisticApi } from "@exagens/behavioral-insight-console-client/dist/apis/SparxsStatisticApi";
import type { StatementsStatisticApi } from "@exagens/behavioral-insight-console-client/dist/apis/StatementsStatisticApi";
import type { UserStatisticApi } from "@exagens/behavioral-insight-console-client/dist/apis/UserStatisticApi";
import {
  createSparxEditorApiConfiguration,
  getOpenApiConfiguration,
} from "@/store/consoleApi/consoleOpenApiConfiguration";
import type { ConsoleApiClients } from "@/store/consoleApi/consoleApiClientTypes";

const loadApi = <K extends keyof ConsoleApiClients>(
  importAndConstruct: (
    configuration: ReturnType<typeof getOpenApiConfiguration>,
  ) => Promise<ConsoleApiClients[K]>,
): Promise<ConsoleApiClients[K]> =>
  importAndConstruct(getOpenApiConfiguration());

let nonSettingsConsoleApiAccessAllowed = false;

let pSettings: Promise<SettingsApi> | null = null;
let pSparxEditor: Promise<SparxEditorApi> | null = null;
let pSparxsStatistic: Promise<SparxsStatisticApi> | null = null;
let pStatementStatistic: Promise<StatementsStatisticApi> | null = null;
let pUserStatistic: Promise<UserStatisticApi> | null = null;

/**
 * When `false`, {@link getSparxEditorApi}, statistic/catalog getters, etc. throw until
 * set back to `true` once shell bootstrap has loaded the signed-in user ({@link bootstrapSessionAuth});
 * cleared on {@link onClientSessionEnd}).
 * {@link getSettingsApi} is always allowed. Clearing access resets lazy client promises.
 */
export const setConsoleApiNonSettingsAccessAllowed = (
  allowed: boolean,
): void => {
  nonSettingsConsoleApiAccessAllowed = allowed;
  if (!allowed) {
    pSparxEditor = null;
    pSparxsStatistic = null;
    pStatementStatistic = null;
    pUserStatistic = null;
  }
};

const assertNonSettingsConsoleApiAccessAllowed = (): void => {
  if (!nonSettingsConsoleApiAccessAllowed) {
    throw new Error(
      "Console API clients other than settings are unavailable until the session is ready.",
    );
  }
};

export const getSettingsApi = (): Promise<SettingsApi> =>
  (pSettings ??= loadApi<"settingsApi">(async (configuration) => {
    const { SettingsApi: SettingsApiClass } =
      await import("@exagens/behavioral-insight-console-client/dist/apis/SettingsApi");
    return new SettingsApiClass(configuration);
  }));

export const getSparxEditorApi = (): Promise<SparxEditorApi> => {
  assertNonSettingsConsoleApiAccessAllowed();
  return (pSparxEditor ??= (async () => {
    const { SparxEditorApi: SparxEditorApiClass } =
      await import("@exagens/behavioral-insight-console-client/dist/apis/SparxEditorApi");
    return new SparxEditorApiClass(createSparxEditorApiConfiguration());
  })());
};

export const getSparxsStatisticApi = (): Promise<SparxsStatisticApi> => {
  assertNonSettingsConsoleApiAccessAllowed();
  return (pSparxsStatistic ??= loadApi<"sparxsStatisticApi">(
    async (configuration) => {
      const { SparxsStatisticApi: SparxsStatisticApiClass } =
        await import("@exagens/behavioral-insight-console-client/dist/apis/SparxsStatisticApi");
      return new SparxsStatisticApiClass(configuration);
    },
  ));
};

export const getStatementsStatisticApi =
  (): Promise<StatementsStatisticApi> => {
    assertNonSettingsConsoleApiAccessAllowed();
    return (pStatementStatistic ??= loadApi<"statementStatisticApi">(
      async (configuration) => {
        const { StatementsStatisticApi: StatementsStatisticApiClass } =
          await import("@exagens/behavioral-insight-console-client/dist/apis/StatementsStatisticApi");
        return new StatementsStatisticApiClass(configuration);
      },
    ));
  };

export const getUserStatisticApi = (): Promise<UserStatisticApi> => {
  assertNonSettingsConsoleApiAccessAllowed();
  return (pUserStatistic ??= loadApi<"userStatisticApi">(
    async (configuration) => {
      const { UserStatisticApi: UserStatisticApiClass } =
        await import("@exagens/behavioral-insight-console-client/dist/apis/UserStatisticApi");
      return new UserStatisticApiClass(configuration);
    },
  ));
};
